import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  styled,
  CircularProgress,
  Alert,
  Menu,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  TableRestaurant as TableRestaurantIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Restaurant as RestaurantIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Print as PrintIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getScreenPermissionsByType } from '../../services/migration/personnelDataService';
import MenuIcon from '@mui/icons-material/Menu';
import MobileMenu from '../layout/MobileMenu';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';
import firebaseDataService from '../../services/FirebaseDataService';
import { mainApi } from '../../config/apiClient';
import * as signalR from '@microsoft/signalr';
import { infiniteRetryPolicy, startResilientConnection } from '../../utils/signalRConnection';
import { usePrintReceipt } from '../qr-menu/components/PrintReceiptService';
import { useAutoPrintService } from './AutoPrintService';
import { KitchenScreenSettingsData, OrderCardColorSettings } from '../qr-menu/components/KitchenScreenSettings';
import { getAccessToken } from '../../services/auth/ownAuthSession';

// Mutfak ekranı için özel Header bileşeni
const KitchenHeader = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  zIndex: 1000,
  backgroundColor: 'white',
  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 3),
  // Desktop: tek satır
  height: '64px',
  flexDirection: 'row',
  // Mobil: iki satır
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(1),
  },
}));

const UserSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  // Mobil: daha küçük gap ve wrap
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
}));

const UserChip = styled(Chip)(({ theme }) => ({
  height: 42,
  borderRadius: 21,
  padding: theme.spacing(0.5, 1),
  backgroundColor: '#0D47A1',
  transition: 'all 0.2s ease-in-out',
  '& .MuiChip-label': {
    color: 'white',
    fontWeight: 'bold',
    transition: 'color 0.2s ease-in-out',
  },
  '&:hover': {
    backgroundColor: 'white',
    '& .MuiChip-label': {
      color: '#0D47A1',
    },
  },
  cursor: 'pointer'
}));

// Styled Components
const KitchenContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: '#f5f5f5',
  overflow: 'hidden'
}));

const ContentArea = styled(Box)<{ isFullscreen?: boolean }>(({ theme, isFullscreen }) => ({
  flex: 1,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingTop: '80px', // KitchenHeader (64px) + küçük boşluk
  overflow: isFullscreen ? 'hidden' : 'auto', // Tam ekranda scroll'u kapat
  height: '100vh', // Tam viewport height kullan
  // Mobil: header iki satır olduğu için daha fazla paddingTop
  [theme.breakpoints.down('md')]: {
    paddingTop: '110px', // Mobil header iki satır - daha fazla alan
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

const OrderCard = styled(Card)<{ 
  status: string; 
  elapsedMinutes: number; 
  isFullscreen?: boolean; 
  screenSize?: 'small' | 'medium' | 'large';
  cardColors?: { backgroundColor: string; borderColor: string; timeColor: string };
  viewMode?: 'detailed' | 'compact';
}>(({ theme, status, elapsedMinutes, isFullscreen, screenSize, cardColors, viewMode = 'detailed' }) => {
  // Dinamik renkleri kullan, yoksa varsayılan renkleri kullan
  let backgroundColor = cardColors?.backgroundColor || '#ffffff';
  let borderColor = cardColors?.borderColor || '#e0e0e0';
  
  // Eğer cardColors yoksa eski sistem (fallback)
  if (!cardColors) {
    if (status === 'Beklemede') {
      if (elapsedMinutes <= 5) {
        backgroundColor = '#e8f5e8';
        borderColor = '#4caf50';
      } else if (elapsedMinutes <= 15) {
        backgroundColor = '#fff3e0';
        borderColor = '#ff9800';
      } else if (elapsedMinutes <= 25) {
        backgroundColor = '#fce4ec';
        borderColor = '#e91e63';
      } else {
        backgroundColor = '#ffebee';
        borderColor = '#f44336';
      }
    } else if (status === 'Hazırlanıyor') {
      backgroundColor = '#fff8e1';
      borderColor = '#ffc107';
    } else if (status === 'Hazır') {
      backgroundColor = '#e3f2fd';
      borderColor = '#2196f3';
    } else if (status === 'Teslim Edildi') {
      backgroundColor = '#f3e5f5';
      borderColor = '#9c27b0';
    } else if (status === 'İptal') {
      backgroundColor = '#fafafa';
      borderColor = '#9e9e9e';
    }
  }

  // Ekran boyutuna göre dinamik yükseklik hesaplaması
  let cardHeight: string;
  let minHeight: string;
  
  // KOMPAKT MODDA DAHA KÜÇÜK KARTLAR
  if (viewMode === 'compact') {
    if (isFullscreen) {
      cardHeight = 'auto';
      minHeight = '180px'; // Kompakt modda daha küçük
    } else {
      switch (screenSize) {
        case 'small':
          cardHeight = 'auto';
          minHeight = '180px';
          break;
        case 'medium':
          cardHeight = 'auto';
          minHeight = '200px';
          break;
        case 'large':
          cardHeight = 'auto';
          minHeight = '220px';
          break;
        default:
          cardHeight = 'auto';
          minHeight = '200px';
      }
    }
  } else {
    // DETAYLI MODDA NORMAL BOYUTLAR
    if (isFullscreen) {
      // Tam ekran modunda viewport'a göre hesapla
      cardHeight = 'calc((100vh - 64px - 40px) / 2 - 12px)';
      minHeight = '250px';
    } else {
      // Normal modda ekran boyutuna göre
      switch (screenSize) {
        case 'small':
          cardHeight = 'calc((100vh - 64px - 80px) / 2 - 12px)';
          minHeight = '280px';
          break;
        case 'medium':
          cardHeight = 'calc((100vh - 64px - 80px) / 2 - 16px)';
          minHeight = '320px';
          break;
        case 'large':
          cardHeight = 'calc((100vh - 64px - 80px) / 2 - 20px)';
          minHeight = '400px';
          break;
        default:
          cardHeight = '350px';
          minHeight = '300px';
      }
    }
  }

  return {
    height: cardHeight,
    minHeight: minHeight,
    border: `2px solid ${borderColor}`,
    backgroundColor,
    borderRadius: theme.spacing(2),
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[8],
    },
  };
});

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  let backgroundColor = '#9e9e9e';
  let color = '#ffffff';
  
  if (status === 'Beklemede') {
    backgroundColor = '#4caf50';
  } else if (status === 'Hazırlanıyor') {
    backgroundColor = '#ffc107';
    color = '#000000';
  } else if (status === 'Hazır') {
    backgroundColor = '#2196f3';
  } else if (status === 'Teslim Edildi') {
    backgroundColor = '#9c27b0';
  } else if (status === 'İptal') {
    backgroundColor = '#9e9e9e';
  }

  return {
    backgroundColor,
    color,
    fontWeight: 'bold',
    fontSize: '0.75rem',
  };
});

const TimerChip = styled(Chip)<{ 
  elapsedMinutes: number; 
  timeColor?: string;
}>(({ theme, elapsedMinutes, timeColor }) => {
  // Dinamik renk varsa kullan, yoksa eski sistem
  if (timeColor) {
    return {
      backgroundColor: timeColor,
      color: '#ffffff',
      fontWeight: 'bold',
      fontSize: '0.75rem',
    };
  }
  
  // Eski sistem (fallback)
  let backgroundColor = '#4caf50';
  let color = '#ffffff';
  
  if (elapsedMinutes > 25) {
    backgroundColor = '#f44336'; // Kırmızı
  } else if (elapsedMinutes > 15) {
    backgroundColor = '#ff9800'; // Turuncu
  } else if (elapsedMinutes > 5) {
    backgroundColor = '#ffc107'; // Sarı
    color = '#000000';
  }

  return {
    backgroundColor,
    color,
    fontWeight: 'bold',
    fontSize: '0.75rem',
  };
});

const ActionButton = styled(Button)<{ isFullscreen?: boolean }>(({ theme, isFullscreen }) => ({
  fontSize: isFullscreen ? '0.8rem' : '0.85rem',
  padding: theme.spacing(isFullscreen ? 0.8 : 1, isFullscreen ? 1 : 1.2),
  fontWeight: 'bold',
  // Dinamik genişlik - container'ın genişliğine göre büyür
  flex: 1,
  minWidth: '60px', // Minimum genişlik
  maxWidth: '120px' // Maximum genişlik
}));

// Interfaces
interface OrderItem {
  id: string;
  urunAdi: string;
  adet?: number;        // Eski format için
  miktar?: number;      // OrdersPage.tsx ile uyumlu format için
  birimFiyat: number;
  toplamFiyat: number;
  aciklama?: string;
  imageUrl?: string;
  urunResmi?: string;   // OrdersPage.tsx ile uyumlu olması için
  productNote?: string; // ✅ Ürüne özel not (özelleştirme seçenekleri)
}

interface Order {
  id: number;
  siparisId: string;
  masaNumarasi: number;
  masaAdi?: string; // ✅ SQL'den gelen masa adı (Teras 11, Bahçe 16, vb.)
  kullaniciAdi: string;
  siparisDurumu: string;
  siparisOlusturmaTarihi: string;
  siparisGuncellenmeTarihi?: string;
  toplamTutar: number;
  siparisDetaylari: OrderItem[];
  qrMenuId?: string;
  siparisNotu?: string;
  teslimAlanKuryeId?: string;
  teslimEdenKuryeId?: string;
  teslimAlanKuryeAdi?: string;
  teslimEdenKuryeAdi?: string;
}

interface KitchenPermissions {
  accessKitchenScreen: boolean;
  viewAllOrders: boolean;
  updateOrderStatus: boolean;
  cancelOrders: boolean;
  showPrintButton: boolean; // Yazdırma butonu göster/gizle
  listReadyOrders: boolean; // Hazır siparişleri listele (false ise 5 saniyede kaldır)
  waiterMode: boolean; // Garson modu (true ise "Garson Ekranı" başlığı)
  autoPrint: boolean; // Otomatik yazdırma (yeni siparişler geldiğinde otomatik yazdır)
  showPrices: boolean; // Fiyatları göster (false ise hiçbir yerde fiyat görünmez)
}

const KitchenScreen: React.FC = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('salesSystem');
  const { currentUser, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Mobil menü state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // State
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<KitchenPermissions | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDetailModalOpen, setOrderDetailModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userData, setUserData] = useState<{ firstName: string; lastName: string } | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [courierNames, setCourierNames] = useState<Map<string, string>>(new Map()); // Kurye ID -> İsim mapping
  const [viewMode, setViewMode] = useState<'detailed' | 'compact'>('detailed'); // Görünüm modu: detaylı veya kompakt
  
  // Mutfak ekranı ayarları
  const [kitchenScreenSettings, setKitchenScreenSettings] = useState<KitchenScreenSettingsData>({
    orderCardColors: {
      pending: {
        '0-5': { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
        '6-15': { backgroundColor: '#fff3e0', borderColor: '#ff9800', timeColor: '#f57c00' },
        '16-25': { backgroundColor: '#fce4ec', borderColor: '#e91e63', timeColor: '#c2185b' },
        '25+': { backgroundColor: '#ffebee', borderColor: '#f44336', timeColor: '#d32f2f' }
      },
      preparing: { backgroundColor: '#fff8e1', borderColor: '#ffc107', timeColor: '#f9a825' },
      ready: { backgroundColor: '#e8f5e8', borderColor: '#4caf50', timeColor: '#2e7d32' },
      delivered: { backgroundColor: '#f3e5f5', borderColor: '#9c27b0', timeColor: '#7b1fa2' },
      cancelled: { backgroundColor: '#fafafa', borderColor: '#9e9e9e', timeColor: '#616161' }
    },
    timeRanges: { range1: 5, range2: 15, range3: 25 }
  });
  
  // SignalR connection
  const [connectionStatus, setConnectionStatus] = useState<string>('Disconnected');
  
  // Teslim edilen siparişleri takip etmek için
  const [deliveredOrders, setDeliveredOrders] = useState<Set<number>>(new Set());
  
  // SignalR'dan gelen siparişleri takip etmek için (duplicate önleme)
  const processedSignalROrders = useRef<Set<string>>(new Set());

  // Sipariş kaynağını belirle (masa numarası null ise sipariş numarasına bak)
  const getOrderSource = (order: Order): string => {
    // Eğer masa numarası varsa, masa adını veya numarasını göster
    if (order.masaNumarasi && order.masaNumarasi > 0) {
      // ✅ Önce masaAdi'yi kontrol et (SQL'den gelen: Teras 11, Bahçe 16, vb.)
      if (order.masaAdi) {
        return order.masaAdi;
      }
      // ✅ masaAdi yoksa eskiden olduğu gibi "Masa X" göster
      return `Masa ${order.masaNumarasi}`;
    }
    
    // Masa numarası null/0 ise sipariş numarasına bak
    if (order.siparisId.includes('-RS-')) {
      // ✅ Restoran Hızlı Satış siparişleri (Örnek: RS-20260109-142555-103-RS-1)
      return 'Restoran Satışı';
    } else if (order.siparisId.includes('-HS-')) {
      // ✅ Hızlı Satış siparişleri (Örnek: HS-20260109-141039-817-HS-19)
      return 'Hızlı Satış';
    } else if (order.siparisId.startsWith('HC-')) {
      // Hacıoğlu entegrasyon siparişleri
      return 'Entegrasyon';
    } else if (order.siparisId.startsWith('CC')) {
      return 'Çağrı Merkezi';
    } else if (order.siparisId.startsWith('EC')) {
      return 'E-ticaret';
    } else if (order.siparisId.startsWith('P-')) {
      // Kiosk Paket Siparişi (Prefix: P-)
      // Örnek: P-20251021001 veya P-1
      return 'Kiosk - Paket';
    } else if (order.siparisId.startsWith('D-')) {
      // Kiosk Masa Siparişi (Prefix: D-)
      // Örnek: D-20251021001 veya D-1
      return 'Kiosk - Masa';
    } else if (order.siparisId.includes('-QR-')) {
      // ✅ QR menü siparişi (ör: SP20260428153022-QR-1)
      // Masa numarası Tables tablosunda eslesmezse bile
      // "E-ticaret" olarak duşmemesi icin bu kontrolu kalici olarak
      // ekledik - masa ataması yoksa jenerik "QR Menü" label'ı gosterilir.
      return 'QR Menü';
    } else {
      // Varsayılan olarak E-ticaret (eski siparişler için)
      return 'E-ticaret';
    }
  };

  // Sipariş ID'sinden kısa kodu çıkar
  const getShortOrderCode = (siparisId: string): string => {
    // RS siparişleri: "RS-20260109-142555-103-RS-1" → "RS-1"
    if (siparisId.includes('-RS-')) {
      const parts = siparisId.split('-RS-');
      if (parts.length === 2) {
        return `RS-${parts[1]}`;
      }
    }
    
    // HS siparişleri: "HS-20260109-141039-817-HS-19" → "HS-19"
    if (siparisId.includes('-HS-')) {
      const parts = siparisId.split('-HS-');
      if (parts.length === 2) {
        return `HS-${parts[1]}`;
      }
    }
    
    // HC siparişleri: "HC-12345" → "HC-12345"
    if (siparisId.startsWith('HC-')) {
      return siparisId; // Tüm kodu göster
    }
    
    // QR Menu siparişleri: "SP202510310520079697-QR-5" → "QR-5"
    if (siparisId.includes('-QR-')) {
      const parts = siparisId.split('-QR-');
      if (parts.length === 2) {
        return `QR-${parts[1]}`;
      }
    }
    
    // Kiosk siparişleri: "D-20251023003-3" → "D-3"
    if (siparisId.startsWith('D-') || siparisId.startsWith('P-')) {
      const parts = siparisId.split('-');
      if (parts.length >= 2) {
        return `${parts[0]}-${parts[parts.length - 1]}`;
      }
    }
    
    // Diğer formatlar (CC, EC, SP vb.) için son 4-6 hanesi göster
    if (siparisId.length > 10) {
      return siparisId.substring(siparisId.length - 6);
    }
    
    return siparisId;
  };
  
  // Yazdırma servisi
  const { printOrderReceipt, printOrderReceiptByCategory } = usePrintReceipt({
    companyId: null, // Artık localStorage kullandığımız için gerekli değil
    menuId: menuId || null,
    onFetchSettingsError: (error) => {
      console.error('❌ [PRINT SERVICE] Yazdırma ayarları yükleme hatası:', error);
    },
    onPrintError: (error) => {
      console.error('❌ [PRINT SERVICE] Yazdırma hatası:', error);
    },
    onPrintSuccess: (message) => {
      console.log('✅ [PRINT SERVICE] Yazdırma başarılı:', message);
    },
    lazyLoad: true // Ayarları sadece yazdırma sırasında yükle
  });
  
  console.log('🖨️ [PRINT SERVICE] Yazdırma servisi başlatıldı:', {
    companyId: null,
    menuId: menuId,
    printOrderReceipt: !!printOrderReceipt,
    printOrderReceiptByCategory: !!printOrderReceiptByCategory
  });

  // Sipariş adisyonunu yazdır
  const handlePrintOrder = useCallback(async (order: Order) => {
    console.log('🖨️ [KITCHEN PRINT] Yazdır butonu tıklandı - Sipariş:', order.siparisId);
    console.log('🖨️ [KITCHEN PRINT] Sipariş detayları:', {
      siparisId: order.siparisId,
      masaNumarasi: order.masaNumarasi,
      kullaniciAdi: order.kullaniciAdi,
      toplamTutar: order.toplamTutar,
      siparisDetaylari: order.siparisDetaylari,
      siparisNotu: order.siparisNotu
    });
    
    try {
      console.log('🖨️ [KITCHEN PRINT] Yazdırma işlemi başlatılıyor...');
      
      // Eğer müşteri adı "anonim" ise masa adını kullan, e-ticaret siparişleri için özel isim
      const customerName = order.kullaniciAdi.toLowerCase() === 'anonim' 
        ? (order.masaNumarasi === 0 || order.masaNumarasi === null ? 'E-ticaret Siparişi' : `Masa ${order.masaNumarasi}`)
        : order.kullaniciAdi;
      
      console.log('🖨️ [KITCHEN PRINT] Müşteri adı düzenlendi:', order.kullaniciAdi, '->', customerName);
      
      const orderData = {
        siparisId: order.siparisId,
        masaNumarasi: order.masaNumarasi,
        kullaniciAdi: customerName, // Düzenlenmiş müşteri adı
        toplamTutar: order.toplamTutar,
        siparisDetaylari: order.siparisDetaylari,
        siparisNotu: order.siparisNotu,
        siparisOlusturmaTarihi: order.siparisOlusturmaTarihi
      };

      console.log('🖨️ [KITCHEN PRINT] Yazdırma verisi hazırlandı:', orderData);
      console.log('🖨️ [KITCHEN PRINT] printOrderReceiptByCategory fonksiyonu çağrılıyor...');

      // Kategori bazlı yazdırma fonksiyonunu kullan
      // Bu fonksiyon içinde printingMode kontrolü yapılıyor
      await printOrderReceiptByCategory(orderData);
      
      console.log('✅ [KITCHEN PRINT] Yazdırma işlemi başarıyla tamamlandı');
    } catch (error) {
      console.error('❌ [KITCHEN PRINT] Yazdırma hatası:', error);
      console.error('❌ [KITCHEN PRINT] Hata detayı:', error instanceof Error ? error.message : 'Bilinmeyen hata');
      console.error('❌ [KITCHEN PRINT] Hata stack:', error instanceof Error ? error.stack : 'Stack yok');
      throw error; // AutoPrintService'in hata durumunu yakalaması için
    }
  }, [printOrderReceiptByCategory]);

  // Otomatik yazdırma servisi
  const { addToQueue: addToPrintQueue } = useAutoPrintService({
    onPrintOrder: handlePrintOrder
  });
  
  console.log('🖨️ [AUTO PRINT SERVICE] Otomatik yazdırma servisi başlatıldı:', {
    addToPrintQueue: !!addToPrintQueue,
    handlePrintOrder: !!handlePrintOrder
  });

  // Login'den gelip gelmediğini kontrol et
  const isFromLogin = location.state?.fromLogin === true;
  
  // Manager'dan gelip gelmediğini kontrol et (referrer URL'den)
  const isFromManager = document.referrer.includes('/manager') || location.state?.fromManager === true;

  // Mutfak ekranı ayarlarını SQL'den yükle
  const loadKitchenScreenSettings = async () => {
    if (!companyId || !menuId) return;

    try {
      console.log(`🎨 Mutfak ekranı ayarları yükleniyor: /api/QRMenu/company/${companyId}/menu-definitions/${menuId}`);
      
      const response = await mainApi.get(`/api/QRMenu/menu-definitions/${menuId}`);
      const result = response.data;
      if (result.success && result.data && result.data.settings) {
        try {
          const settings = JSON.parse(result.data.settings);
          if (settings.kitchenScreenSettings) {
            console.log('✅ Mutfak ekranı ayarları yüklendi:', settings.kitchenScreenSettings);
            setKitchenScreenSettings(settings.kitchenScreenSettings);
          } else {
            console.log('ℹ️ Mutfak ekranı ayarları bulunamadı, varsayılan ayarlar kullanılıyor');
          }
        } catch (parseError) {
          console.warn('❌ Mutfak ekranı ayarları parse hatası:', parseError);
        }
      }
    } catch (error) {
      console.error('❌ Mutfak ekranı ayarları yükleme hatası:', error);
    }
  };

  // Zaman güncelleyici - daha sık güncelle
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // Her 10 saniyede güncelle

    return () => clearInterval(timer);
  }, []);

  // Şirket ID'sini ve kullanıcı bilgilerini al
  useEffect(() => {
    const fetchUserData = async () => {
      console.log('🔍 [KitchenScreen] Kullanıcı bilgileri alınıyor:', { currentUser: !!currentUser, uid: currentUser?.uid });
      
      if (currentUser) {
        try {
          const data = await firebaseDataService.getUserData(currentUser.uid);
          if (data) {
            console.log('✅ [KitchenScreen] Kullanıcı bilgileri alındı:', { companyId: data.companyId, role: data.role });
            setCompanyId(data.companyId || '');
            setUserData({
              firstName: data.firstName || '',
              lastName: data.lastName || ''
            });
          } else {
            console.log('❌ [KitchenScreen] Firebase kullanıcı bilgisi bulunamadı');
          }
        } catch (error) {
          console.error('❌ [KitchenScreen] Kullanıcı bilgisi alma hatası:', error);
          setError('Kullanıcı bilgileri alınamadı.');
        }
      } else {
        console.log('❌ [KitchenScreen] CurrentUser yok');
      }
    };
    fetchUserData();
  }, [currentUser]);

  // Kullanıcı yetkilerini kontrol et
  useEffect(() => {
    const checkPermissions = async () => {
      console.log('🔍 [KitchenScreen] Yetki kontrolü başlatılıyor:', { 
        currentUser: !!currentUser, 
        companyId, 
        menuId,
        isFromManager 
      });
      
      if (!currentUser || !companyId || !menuId) {
        console.log('❌ [KitchenScreen] Eksik bilgi:', { currentUser: !!currentUser, companyId, menuId });
        return;
      }

      try {
        // Admin kullanıcısı için tüm yetkileri ver
        const userData = await firebaseDataService.getUserData(currentUser.uid);
        if (userData) {
          if (userData.role === 'admin') {
            const adminPermissions = {
              accessKitchenScreen: true,
              viewAllOrders: true,
              updateOrderStatus: true,
              cancelOrders: true,
              showPrintButton: true, // Admin için tüm özellikler açık
              listReadyOrders: true,
              waiterMode: false, // Admin için garson modu varsayılan kapalı
              autoPrint: false, // Admin için otomatik yazdırma varsayılan kapalı
              showPrices: true // Admin için fiyatları göster varsayılan açık
            };
            console.log('🔑 [PERMISSIONS] Admin yetkileri ayarlandı:', adminPermissions);
            setPermissions(adminPermissions);
            return;
          }
          
          // Manager kullanıcısı için de tüm yetkileri ver
          if (userData.role === 'manager') {
            console.log('🔑 [KitchenScreen] Manager kullanıcısı tespit edildi, tüm yetkiler veriliyor');
            const managerPermissions = {
              accessKitchenScreen: true,
              viewAllOrders: true,
              updateOrderStatus: true,
              cancelOrders: true,
              showPrintButton: true,
              listReadyOrders: true,
              waiterMode: false,
              autoPrint: false,
              showPrices: true
            };
            console.log('🔑 [PERMISSIONS] Manager yetkileri ayarlandı:', managerPermissions);
            setPermissions(managerPermissions);
            console.log('✅ [KitchenScreen] Manager yetkileri ayarlandı');
            return;
          }
          
          console.log('🔍 [KitchenScreen] Kullanıcı rolü:', userData.role);
        }

        // Faz 3: personel kitchenScreens yetkisi SQL'den; HybridFallback ile RTDB.
        let userPermissions: any = null;
        try {
          const rows = await getScreenPermissionsByType(currentUser.uid, 'kitchenScreens');
          const row = rows.find(r => String(r.screenId) === String(menuId) && r.isEnabled);
          if (row && row.settings) userPermissions = row.settings;
        } catch (e) {
          console.warn('[KitchenScreen] SQL yetki okuma hatasi:', e);
        }
        if (userPermissions) {
          console.log('✅ [KitchenScreen] Personel yetkileri bulundu:', userPermissions);
          console.log('🔑 [PERMISSIONS] Personel yetkileri ayarlandı:', {
            showPrintButton: userPermissions.showPrintButton,
            autoPrint: userPermissions.autoPrint,
            accessKitchenScreen: userPermissions.accessKitchenScreen
          });
          setPermissions(userPermissions);
          
          if (!userPermissions.accessKitchenScreen) {
            console.log('❌ [KitchenScreen] Mutfak ekranı erişim yetkisi yok');
            setError('Bu mutfak ekranına erişim yetkiniz bulunmamaktadır.');
          }
        } else {
          console.log('❌ [KitchenScreen] Personel yetkileri bulunamadı');
          setError('Mutfak ekranı yetkileri bulunamadı.');
        }
      } catch (error) {
        console.error('❌ [KitchenScreen] Yetki kontrolü hatası:', error);
        setError('Yetki kontrolü sırasında hata oluştu.');
      }
    };

    checkPermissions();
  }, [currentUser, companyId, menuId]);

  // SignalR bağlantısını kur
  useEffect(() => {
    if (!companyId || !permissions) {
      return;
    }

    let stopConnection: (() => void) | null = null;

    const setupSignalRConnection = async () => {
      try {
        const apiUrl = process.env.REACT_APP_NETAPI_URL;
        const connection = new signalR.HubConnectionBuilder()
          .withUrl(`${apiUrl}/ordershub`, {
            accessTokenFactory: async () => (await getAccessToken().catch(() => null)) ?? '',
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect(infiniteRetryPolicy)
          .configureLogging(signalR.LogLevel.None)
          .build();

        connection.on('NewOrder', (orderData) => {
          console.log('🔔 SignalR: Yeni sipariş geldi:', orderData.siparisId, '- ID:', orderData.id);
          if (orderData.qrMenuId === menuId) {
            // Duplicate SignalR event kontrolü
            const orderKey = `${orderData.id}_${orderData.siparisId}`;
            if (processedSignalROrders.current.has(orderKey)) {
              console.log('⏭️ SignalR: DUPLICATE - Sipariş zaten işlendi:', orderData.siparisId);
              return;
            }
            
            // Bu siparişi işlendi olarak işaretle
            processedSignalROrders.current.add(orderKey);
            
            // 30 saniye sonra bu siparişi processed listesinden çıkar (memory leak önleme)
            setTimeout(() => {
              processedSignalROrders.current.delete(orderKey);
            }, 30000);
            
            // Yeni siparişi listeye ekle
            const newOrder: Order = {
              id: orderData.id,
              siparisId: orderData.siparisId,
              masaNumarasi: orderData.masaNumarasi || orderData.masaId,
              masaAdi: orderData.masaAdi, // ✅ SignalR'dan gelen masa adı
              kullaniciAdi: orderData.kullaniciAdi,
              siparisDurumu: orderData.siparisDurumu,
              siparisOlusturmaTarihi: orderData.siparisOlusturmaTarihi,
              toplamTutar: orderData.toplamTutar,
              qrMenuId: orderData.qrMenuId,
              siparisDetaylari: parseSiparisDetaylari(orderData.siparisDetaylari),
              siparisNotu: parseSiparisNotu(orderData.siparisDetaylari, orderData.siparisNotu)
            };
            
             setOrders(prevOrders => {
               // Aynı ID'ye sahip sipariş zaten var mı kontrol et
               const existingOrderIndex = prevOrders.findIndex(order => order.id === newOrder.id);
               
               if (existingOrderIndex !== -1) {
                 // Mevcut siparişi güncelle - YAZDIRMA YOK
                 const updatedOrders = [...prevOrders];
                 updatedOrders[existingOrderIndex] = newOrder;
                 return updatedOrders.sort((a, b) => 
                   new Date(a.siparisOlusturmaTarihi).getTime() - new Date(b.siparisOlusturmaTarihi).getTime()
                 );
               } else {
                 // Yeni sipariş ekle - SADECE BURADA YAZDIR
                 console.log(`➕ SignalR: Yeni sipariş listeye eklendi: ${newOrder.siparisId}`);
                 
                // Otomatik yazdırma - SADECE BURADA!
                console.log('🖨️ [AUTO PRINT] Otomatik yazdırma kontrolü:', {
                  autoPrintPermission: permissions?.autoPrint,
                  siparisId: newOrder.siparisId,
                  permissions: permissions
                });
                
                if (permissions?.autoPrint) {
                  console.log('✅ [AUTO PRINT] Otomatik yazdırma yetkisi var, kuyruğa ekleniyor:', newOrder.siparisId);
                  addToPrintQueue(newOrder);
                  console.log('📋 [AUTO PRINT] Sipariş yazdırma kuyruğuna eklendi');
                } else {
                  console.log('❌ [AUTO PRINT] Otomatik yazdırma yetkisi yok veya kapalı');
                }
                 
                 return [newOrder, ...prevOrders].sort((a, b) => 
                   new Date(a.siparisOlusturmaTarihi).getTime() - new Date(b.siparisOlusturmaTarihi).getTime()
                 );
               }
             });
          }
        });

        connection.on('OrderStatusUpdated', (orderData) => {
          console.log('🔄 SignalR: Sipariş durumu güncelleme bildirimi alındı:', orderData.siparisId, '- Yeni Durum:', orderData.siparisDurumu);
          console.log('🔍 SignalR OrderStatusUpdated - Gelen veri:', orderData);
          console.log('🔍 Menü kontrolü:', orderData.qrMenuId, 'vs', menuId);
          
          // qrMenuId kontrolü yerine mevcut siparişler listesinde bu sipariş var mı kontrol et
          // Not: orders state'i güncel olmayabilir, bu yüzden setOrders callback'inde kontrol edeceğiz
          console.log('🔍 Sipariş kontrolü yapılıyor - ID:', orderData.id, 'siparisId:', orderData.siparisId);
          
          // Her durumda setOrders'ı çağır, içinde kontrol yap
          setOrders(prevOrders => {
            // Önce bu sipariş listede var mı kontrol et
            const isOrderInCurrentList = prevOrders.some(order => order.id === orderData.id || order.siparisId === orderData.siparisId);
            console.log('🔍 Sipariş mevcut listede var mı?', isOrderInCurrentList, '- Aranan ID:', orderData.id, 'veya siparisId:', orderData.siparisId);
            
            // Eğer qrMenuId eşleşiyorsa veya sipariş listede varsa güncelle
            if (orderData.qrMenuId === menuId || isOrderInCurrentList) {
              console.log('✅ SignalR: Sipariş bu menüye ait (qrMenuId eşleşmesi veya listede mevcut), güncelleme yapılıyor:', orderData.siparisId);
              console.log('🔄 Güncelleme öncesi siparişler:', prevOrders.map(o => `${o.siparisId}:${o.siparisDurumu}`));
              
              const updatedOrders = prevOrders.map(order => 
                (order.id === orderData.id || order.siparisId === orderData.siparisId)
                  ? { ...order, siparisDurumu: orderData.siparisDurumu, siparisGuncellenmeTarihi: orderData.siparisGuncellenmeTarihi || new Date().toISOString() }
                  : order
              );
              console.log('🔄 Güncelleme sonrası siparişler:', updatedOrders.map(o => `${o.siparisId}:${o.siparisDurumu}`));
              
              // İptal edilen siparişleri hemen listeden çıkar
              const filteredOrders = updatedOrders.filter(order => order.siparisDurumu !== 'İptal');
              
              // Teslim edilen siparişler için 5 saniye timer başlat
              const deliveredOrder = updatedOrders.find(order => 
                order.id === orderData.id && order.siparisDurumu === 'Teslim Edildi'
              );
              
              if (deliveredOrder && !deliveredOrders.has(deliveredOrder.id)) {
                setDeliveredOrders(prev => new Set(prev).add(deliveredOrder.id));
                
                setTimeout(() => {
                  setOrders(prevOrders => {
                    const filteredOrders = prevOrders.filter(order => order.id !== deliveredOrder.id);
                    return filteredOrders;
                  });
                  setDeliveredOrders(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(deliveredOrder.id);
                    return newSet;
                  });
                }, 5000); // 5 saniye
              }
              
              // Hazır siparişler için de kontrol et (listReadyOrders false ise)
              const readyOrder = updatedOrders.find(order => 
                order.id === orderData.id && order.siparisDurumu === 'Hazır'
              );
              
              if (readyOrder && !deliveredOrders.has(readyOrder.id) && permissions?.listReadyOrders === false) {
                setDeliveredOrders(prev => new Set(prev).add(readyOrder.id));
                
                setTimeout(() => {
                  setOrders(prevOrders => {
                    const filteredOrders = prevOrders.filter(order => order.id !== readyOrder.id);
                    return filteredOrders;
                  });
                  setDeliveredOrders(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(readyOrder.id);
                    return newSet;
                  });
                }, 5000); // 5 saniye
              }
              
              console.log('🔄 SignalR: Sipariş durumu güncellendi, yeni liste:', filteredOrders.map(o => `${o.siparisId}:${o.siparisDurumu}`));
              return filteredOrders;
            } else {
              console.log('⏭️ SignalR: Sipariş bu menüye ait değil veya listede yok, atlanıyor. qrMenuId:', orderData.qrMenuId, 'vs', menuId, '- Listede var mı:', isOrderInCurrentList);
              return prevOrders; // Değişiklik yok
            }
          });
        });

        connection.on('OrderDeleted', (orderData) => {
          if (orderData.qrMenuId === menuId) {
            setOrders(prevOrders => prevOrders.filter(order => order.id !== orderData.id));
          }
        });

        // Gruba katılma onConnected içinde: yeniden bağlanma yeni bir connection id
        // ürettiği için sunucudaki grup üyeliği kaybolur.
        stopConnection = startResilientConnection(connection, {
          label: 'Kitchen',
          onConnected: () => connection.invoke('JoinCompanyGroup', companyId),
          onStatusChange: setConnectionStatus,
        });
      } catch (error) {
        setConnectionStatus('Error');
      }
    };

    setupSignalRConnection();

    return () => {
      stopConnection?.();
    };
  }, [companyId, menuId, permissions]);

  // YAZDIRMA SADECE SignalR'dan geldiğinde yapılacak, başka hiçbir yerde değil!


  // JSON'dan sipariş detaylarını parse et - hem küçük hem büyük harfli field'ları destekle
  const parseSiparisDetaylari = (siparisDetaylari: any) => {
    try {
      let parsedData;
      
      // Eğer string ise JSON parse et
      if (typeof siparisDetaylari === 'string') {
        parsedData = JSON.parse(siparisDetaylari || '[]');
      } else {
        parsedData = siparisDetaylari || [];
      }
      
      // Eğer nested format ise (yeni format: {siparisDetaylari: [...], siparisNotu: "..."})
      if (parsedData.siparisDetaylari && Array.isArray(parsedData.siparisDetaylari)) {
        parsedData = parsedData.siparisDetaylari;
      }
      
      // Eğer array değilse boş array döndür
      if (!Array.isArray(parsedData)) {
        return [];
      }
      
      // Her item için hem küçük hem büyük harfli field'ları kontrol et
      return parsedData.map((item: any) => {
        const imageUrl = item.imageUrl || item.urunResmi || item.UrunResmi || '';
        const urunResmi = item.urunResmi || item.UrunResmi || item.imageUrl || '';
        
        console.log('🖼️ [KITCHEN DEBUG] Ürün resmi parse:', {
          urunAdi: item.urunAdi || item.UrunAdi || '',
          originalImageUrl: item.imageUrl,
          originalUrunResmi: item.urunResmi,
          originalUrunResmiCap: item.UrunResmi,
          finalImageUrl: imageUrl,
          finalUrunResmi: urunResmi,
          hasImage: !!(imageUrl || urunResmi)
        });
        
        return {
          id: item.id || item.urunId || item.UrunId || '',
          urunAdi: item.urunAdi || item.UrunAdi || '',
          adet: item.adet || item.miktar || item.Miktar || 1,
          miktar: item.miktar || item.Miktar || item.adet || 1,
          birimFiyat: item.birimFiyat || item.BirimFiyat || 0,
          toplamFiyat: item.toplamFiyat || item.ToplamFiyat || 0,
          aciklama: item.aciklama || item.Aciklama || '',
          imageUrl: imageUrl,
          urunResmi: urunResmi
        };
      });
    } catch (error) {
      console.error('❌ [KITCHEN] Sipariş detayları parse hatası:', error);
      return [];
    }
  };

  // Sipariş notunu parse et - nested JSON'dan çıkar
  const parseSiparisNotu = (siparisDetaylari: any, siparisNotu: any) => {
    try {
      // Önce direkt sipariş notu varsa onu kullan
      if (siparisNotu) {
        return siparisNotu;
      }
      
      // Eğer siparisDetaylari string ise parse et ve içinde siparisNotu ara
      if (typeof siparisDetaylari === 'string') {
        const parsedData = JSON.parse(siparisDetaylari || '{}');
        if (parsedData.siparisNotu) {
          return parsedData.siparisNotu;
        }
      }
      
      return null;
    } catch (error) {
      return siparisNotu || null;
    }
  };

  // Kurye ismini Firebase'den çek
  const fetchCourierName = async (firebaseUid: string): Promise<string> => {
    try {
      // Önce cache'de var mı kontrol et
      if (courierNames.has(firebaseUid)) {
        return courierNames.get(firebaseUid)!;
      }

      // Firebase'den kullanıcı bilgisini çek
      const userData = await firebaseDataService.getUserData(firebaseUid);
      if (userData) {
        const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Kurye';
        
        // Cache'e ekle
        setCourierNames(prev => new Map(prev).set(firebaseUid, fullName));
        
        return fullName;
      }
      
      return 'Kurye';
    } catch (error) {
      console.error('❌ Kurye ismi çekilirken hata:', error);
      return 'Kurye';
    }
  };

  // Siparişleri yükle (OrdersPage.tsx ile aynı endpoint)
  const fetchOrders = async (showRefreshing = false) => {
    if (!companyId) return;

    if (showRefreshing) setRefreshing(true);
    setError(null);

    try {
      const response = await mainApi.get(`/api/MasaSiparisi/orders`);
      const result = response.data;
      if (result.success && result.data) {
        const allOrders: Order[] = await Promise.all(result.data.map(async (order: any) => {
          let teslimAlanKuryeAdi: string | undefined;
          let teslimEdenKuryeAdi: string | undefined;

          if (order.teslimAlanKuryeId) {
            teslimAlanKuryeAdi = await fetchCourierName(order.teslimAlanKuryeId);
          }

          if (order.teslimEdenKuryeId) {
            teslimEdenKuryeAdi = await fetchCourierName(order.teslimEdenKuryeId);
          }

          return {
            id: order.id,
            siparisId: order.siparisId,
            masaNumarasi: order.masaNumarasi || order.masaId,
            masaAdi: order.masaAdi,
            kullaniciAdi: order.kullaniciAdi,
            siparisDurumu: order.siparisDurumu,
            siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
            siparisGuncellenmeTarihi: order.siparisGuncellenmeTarihi,
            toplamTutar: order.toplamTutar,
            qrMenuId: order.qrMenuId,
            siparisDetaylari: parseSiparisDetaylari(order.siparisDetaylari),
            siparisNotu: parseSiparisNotu(order.siparisDetaylari, order.siparisNotu),
            teslimAlanKuryeId: order.teslimAlanKuryeId,
            teslimEdenKuryeId: order.teslimEdenKuryeId,
            teslimAlanKuryeAdi,
            teslimEdenKuryeAdi
          };
        }));

        const menuOrders = allOrders.filter(order => order.qrMenuId === menuId);

        const activeOrders = menuOrders.filter(order => {
          if (order.siparisDurumu === 'İptal' || order.siparisDurumu === 'Teslim Edildi') {
            return false;
          }
          if (order.siparisDurumu === 'Hazır' && permissions?.listReadyOrders === false) {
            return false;
          }
          return true;
        });

        activeOrders.sort((a, b) => 
          new Date(a.siparisOlusturmaTarihi).getTime() - new Date(b.siparisOlusturmaTarihi).getTime()
        );

        setOrders(activeOrders);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setOrders([]);
      } else {
        setError('Siparişler yüklenirken hata oluştu.');
      }
    } finally {
      setLoading(false);
      if (showRefreshing) setRefreshing(false);
    }
  };

  // Mutfak ekranı ayarlarını yükle
  useEffect(() => {
    if (companyId && menuId) {
      loadKitchenScreenSettings();
    }
  }, [companyId, menuId]);

  // İlk yükleme
  useEffect(() => {
    if (companyId && menuId && permissions?.accessKitchenScreen) {
      fetchOrders();
    }
  }, [companyId, menuId, permissions]);

  // Sipariş durumunu güncelle (OrdersPage.tsx ile aynı)
  const updateOrderStatus = async (orderId: number, newStatus: string) => {
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı.');
      return;
    }

    console.log('🔄 Sipariş durumu güncelleniyor:', orderId, '->', newStatus);
    try {
      const response = await mainApi.post(`/api/MasaSiparisi/order/${orderId}/status`, {
        siparisDurumu: newStatus
      });

      const result = response.data;
      if (result.success) {
        console.log('✅ API: Sipariş durumu başarıyla güncellendi:', orderId, '->', newStatus);
        console.log('⏳ SignalR eventini bekliyoruz, local state güncellenmeyecek...');
        
        try {
          const storedOrders = localStorage.getItem('user_orders');
          if (storedOrders) {
            const orders = JSON.parse(storedOrders);
            const updatedOrders = orders.map((order: any) => 
              order.siparisNo === orderId || order.id === orderId
                ? { ...order, siparisDurumu: newStatus, siparisGuncellenmeTarihi: new Date().toISOString() }
                : order
            );
            localStorage.setItem('user_orders', JSON.stringify(updatedOrders));
          }
        } catch (error) {
          // localStorage güncelleme hatası - sessizce devam et
        }
      } else {
        throw new Error(result.message || 'Durum güncellenemedi');
      }
    } catch (error: any) {
      setError(`Sipariş durumu güncellenirken hata oluştu: ${error.message}`);
    }
  };

  // Geçen süreyi hesapla
  const calculateElapsedMinutes = (orderDate: string): number => {
    const orderTime = new Date(orderDate);
    const now = new Date(); // Her hesaplamada güncel saati al
    const diffMs = now.getTime() - orderTime.getTime();
    const minutes = Math.floor(diffMs / (1000 * 60));
    
    return minutes;
  };

  // Dinamik renk hesaplama fonksiyonu
  const getOrderCardColors = (status: string, elapsedMinutes: number) => {
    const colors = kitchenScreenSettings.orderCardColors;
    const timeRanges = kitchenScreenSettings.timeRanges;
    
    if (status === 'Beklemede') {
      if (elapsedMinutes <= timeRanges.range1) {
        return colors.pending['0-5'];
      } else if (elapsedMinutes <= timeRanges.range2) {
        return colors.pending['6-15'];
      } else if (elapsedMinutes <= timeRanges.range3) {
        return colors.pending['16-25'];
      } else {
        return colors.pending['25+'];
      }
    } else if (status === 'Hazırlanıyor') {
      return colors.preparing;
    } else if (status === 'Hazır') {
      return colors.ready;
    } else if (status === 'Teslim Edildi') {
      return colors.delivered;
    } else if (status === 'İptal') {
      return colors.cancelled;
    }
    
    // Varsayılan renk
    return { backgroundColor: '#ffffff', borderColor: '#e0e0e0', timeColor: '#666666' };
  };

  // Sipariş detaylarını aç
  const handleOpenOrderDetail = (order: Order) => {
    setSelectedOrder(order);
    setOrderDetailModalOpen(true);
  };

  // Geri git
  const handleGoBack = () => {
    // Browser history'den bir önceki sayfaya git
    // Eğer history yoksa varsayılan sayfaya yönlendir
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // History yoksa varsayılan sayfaya git
      navigate('/qr-menu');
    }
  };

  // Kullanıcı menüsü
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleUserMenuClose();
      navigate('/login');
    } catch (error) {
      // Çıkış hatası - sessizce devam et
    }
  };

  const userFullName = userData 
    ? `${userData.firstName} ${userData.lastName}`.trim() || 'Kullanıcı'
    : 'Kullanıcı';

  // Tam ekran toggle fonksiyonu
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      // Tam ekrana geç
      document.documentElement.requestFullscreen().catch((err) => {
        console.error('Tam ekran moduna geçilemedi:', err);
      });
    } else {
      // Tam ekrandan çık
      document.exitFullscreen().catch((err) => {
        console.error('Tam ekran modundan çıkılamadı:', err);
      });
    }
  }, []);

  // Fullscreen değişikliklerini dinle ve F11 tuşu desteği
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // F11 tuşuna basıldığında
      if (event.key === 'F11') {
        event.preventDefault(); // Tarayıcının varsayılan F11 davranışını engelle
        toggleFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleFullscreen]);

  // Responsive ekran boyutu state'i
  const [screenSize, setScreenSize] = useState<'small' | 'medium' | 'large'>('medium');

  // Ekran boyutunu tespit et
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      if (width < 1200 || height < 700) {
        setScreenSize('small');
      } else if (width < 1600 || height < 900) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };

    // İlk yükleme
    handleResize();

    // Pencere boyutu değiştiğinde kontrol et
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (loading) {
    return (
      <KitchenContainer>
        <KitchenHeader>
          {/* Üst Satır */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            gap: { xs: 1, md: 2 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
              <IconButton onClick={handleGoBack} size="small" sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                <ArrowBackIcon />
              </IconButton>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleGoBack} size="small" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                {t('kitchenScreen.header.back')}
              </Button>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#0D47A1', fontSize: { xs: '0.9rem', md: '1.25rem' } }}>
                {permissions?.waiterMode ? t('kitchenScreen.header.waiter') : t('kitchenScreen.header.kitchen')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
              <CircularProgress size={20} />
              <IconButton onClick={handleMobileMenuToggle} sx={{ display: { xs: 'inline-flex', md: 'none' }, bgcolor: '#0D47A1', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'white', color: '#0D47A1', border: '2px solid #0D47A1' } }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
          {/* Alt Satır */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' }, gap: { xs: 0.5, md: 1 }, flexWrap: 'wrap', width: '100%' }}>
            <Chip label={isMobile ? t('kitchenScreen.header.mobileScreenCount', { count: 0 }) : t('kitchenScreen.header.screenCount', { count: 0 })} color="primary" variant="filled" size="small" sx={{ fontSize: { xs: '0.7rem', md: '0.8125rem' } }} />
            <Chip label={isMobile ? '🔴' : '🔴 Bağlantı Yok'} color="error" variant="filled" size="small" sx={{ fontSize: { xs: '0.7rem', md: '0.8125rem' } }} />
          </Box>
        </KitchenHeader>
        
        {/* Mobil Menü Drawer - Loading */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
          }}
        >
          <MobileMenu onClose={handleMobileMenuToggle} />
        </Drawer>
        
        <ContentArea isFullscreen={isFullscreen}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress size={60} />
          </Box>
        </ContentArea>
      </KitchenContainer>
    );
  }

  if (error) {
    return (
      <KitchenContainer>
        <KitchenHeader>
          {/* Üst Satır */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            gap: { xs: 1, md: 2 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
              <IconButton onClick={handleGoBack} size="small" sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                <ArrowBackIcon />
              </IconButton>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleGoBack} size="small" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                {t('kitchenScreen.header.back')}
              </Button>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#0D47A1', fontSize: { xs: '0.9rem', md: '1.25rem' } }}>
                {permissions?.waiterMode ? t('kitchenScreen.header.waiter') : t('kitchenScreen.header.kitchen')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
              <IconButton onClick={() => window.location.reload()} size="small" sx={{ display: { xs: 'inline-flex', md: 'none' } }} title={t('kitchenScreen.header.refresh')}>
                <RefreshIcon />
              </IconButton>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => window.location.reload()} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                {t('kitchenScreen.header.refresh')}
              </Button>
              <IconButton onClick={handleMobileMenuToggle} sx={{ display: { xs: 'inline-flex', md: 'none' }, bgcolor: '#0D47A1', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'white', color: '#0D47A1', border: '2px solid #0D47A1' } }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
          {/* Alt Satır */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' }, gap: { xs: 0.5, md: 1 }, flexWrap: 'wrap', width: '100%' }}>
            <Chip label={isMobile ? t('kitchenScreen.header.mobileScreenCount', { count: 0 }) : t('kitchenScreen.header.screenCount', { count: 0 })} color="primary" variant="filled" size="small" sx={{ fontSize: { xs: '0.7rem', md: '0.8125rem' } }} />
            <Chip label={isMobile ? '🔴' : '🔴 Bağlantı Yok'} color="error" variant="filled" size="small" sx={{ fontSize: { xs: '0.7rem', md: '0.8125rem' } }} />
          </Box>
        </KitchenHeader>
        
        {/* Mobil Menü Drawer - Error */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
          }}
        >
          <MobileMenu onClose={handleMobileMenuToggle} />
        </Drawer>
        
        <ContentArea isFullscreen={isFullscreen}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </ContentArea>
      </KitchenContainer>
    );
  }

  // Sipariş sayıları hesaplama (header'da kullanılacak)
  const maxDisplayOrders = viewMode === 'compact' ? 24 : 12;
  const displayedOrders = Math.min(orders.length, maxDisplayOrders);
  const waitingOrders = Math.max(0, orders.length - maxDisplayOrders);

  return (
    <KitchenContainer>
      <KitchenHeader>
        {/* Üst Satır: Geri + Başlık + Aksiyonlar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          gap: { xs: 1, md: 2 }
        }}>
          {/* Sol: Geri + Başlık */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 } }}>
            {/* Mobilde sadece ikon */}
            <IconButton
              onClick={handleGoBack}
              size="small"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            {/* Desktop'ta buton */}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              size="small"
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {t('kitchenScreen.header.back')}
            </Button>
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              sx={{ 
                color: '#0D47A1',
                fontSize: { xs: '0.9rem', md: '1.25rem' }
              }}
            >
              {permissions?.waiterMode ? t('kitchenScreen.header.waiter') : t('kitchenScreen.header.kitchen')}
            </Typography>
          </Box>

          {/* Sağ: Aksiyonlar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
            {/* Rehber (i) — dokunmatik için en az 40px; tabIndex=-1: odağı almaz */}
            <GuideButton
              guideKey="sales.kitchenScreen"
              color="primary"
              tabIndex={-1}
              sx={{ width: 40, height: 40 }}
            />
            {/* Yenile - Mobilde sadece ikon */}
            <IconButton
              onClick={() => fetchOrders(true)}
              disabled={refreshing}
              size="small"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              title={t('kitchenScreen.header.refresh')}
            >
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={() => fetchOrders(true)}
              disabled={refreshing}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {t('kitchenScreen.header.refresh')}
            </Button>
            
            {/* Kompakt/Detaylı - Mobilde sadece ikon */}
            <IconButton
              onClick={() => setViewMode(viewMode === 'detailed' ? 'compact' : 'detailed')}
              size="small"
              color={viewMode === 'compact' ? 'warning' : 'primary'}
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              title={viewMode === 'detailed' ? t('kitchenScreen.header.compactMode') : t('kitchenScreen.header.detailedMode')}
            >
              {viewMode === 'detailed' ? <ViewListIcon /> : <ViewModuleIcon />}
            </IconButton>
            <Button
              variant="outlined"
              startIcon={viewMode === 'detailed' ? <ViewListIcon /> : <ViewModuleIcon />}
              onClick={() => setViewMode(viewMode === 'detailed' ? 'compact' : 'detailed')}
              title={viewMode === 'detailed' ? t('kitchenScreen.header.switchToCompact') : t('kitchenScreen.header.switchToDetailed')}
              color={viewMode === 'compact' ? 'warning' : 'primary'}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {viewMode === 'detailed' ? t('kitchenScreen.header.compact') : t('kitchenScreen.header.detailed')}
            </Button>
            
            {/* Tam Ekran - Mobilde sadece ikon */}
            <IconButton
              onClick={toggleFullscreen}
              size="small"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              title={isFullscreen ? t('kitchenScreen.header.exitFullscreen') : t('kitchenScreen.header.fullscreen')}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <Button
              variant="outlined"
              startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              onClick={toggleFullscreen}
              title={isFullscreen ? t('kitchenScreen.header.exitFullscreenWithShortcut') : t('kitchenScreen.header.fullscreenWithShortcut')}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {isFullscreen ? t('kitchenScreen.header.exit') : t('kitchenScreen.header.fullscreen')}
            </Button>
            
            {/* SignalR Bağlantı Durumu - Üst satırda nokta */}
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: connectionStatus === 'Connected' ? '#4caf50' : '#f44336',
                boxShadow: connectionStatus === 'Connected' 
                  ? '0 0 8px #4caf50' 
                  : '0 0 8px #f44336',
              }}
              title={connectionStatus === 'Connected' ? t('kitchenScreen.header.serverConnected') : t('kitchenScreen.header.serverDisconnected')}
            />
            
            {/* Kullanıcı - Desktop'ta isimli chip, Mobilde avatar ikon */}
            <UserChip
              label={userFullName}
              onClick={handleUserMenuOpen}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            />
            <IconButton
              onClick={handleUserMenuOpen}
              size="small"
              sx={{ 
                display: { xs: 'inline-flex', md: 'none' },
                bgcolor: '#0D47A1',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: 'white',
                  color: '#0D47A1',
                  border: '1px solid #0D47A1'
                }
              }}
              title={userFullName}
            >
              <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'transparent', color: 'inherit' }}>
                {userFullName.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            
            {/* Mobil Menü Butonu */}
            <IconButton
              onClick={handleMobileMenuToggle}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                bgcolor: '#0D47A1',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': {
                  bgcolor: 'white',
                  color: '#0D47A1',
                  border: '2px solid #0D47A1'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        {/* İkinci Satır: Sipariş Sayıları (hem mobil hem desktop) */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: { xs: 'center', md: 'flex-end' },
          gap: { xs: 0.5, md: 1 },
          flexWrap: 'wrap',
          width: '100%'
        }}>
          {/* Ekranda */}
          <Chip 
            label={t('kitchenScreen.header.screenCount', { count: displayedOrders })}
            color="primary"
            variant="filled"
            size="small"
            title={t('kitchenScreen.header.screenCountTooltip', { count: displayedOrders })}
            sx={{ fontSize: { xs: '0.7rem', md: '0.8125rem' } }}
          />
          {/* Sırada */}
          {waitingOrders > 0 && (
            <Chip 
              label={t('kitchenScreen.header.waitingCount', { count: waitingOrders })}
              color="warning"
              variant="filled"
              size="small"
              title={t('kitchenScreen.header.waitingCountTooltip', { count: waitingOrders })}
              sx={{ 
                fontSize: { xs: '0.7rem', md: '0.8125rem' },
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                  '100%': { opacity: 1 }
                }
              }}
            />
          )}
          {/* Toplam */}
          <Chip 
            label={t('kitchenScreen.header.totalCount', { count: orders.length })}
            color="default"
            variant="outlined"
            size="small"
            title={t('kitchenScreen.header.totalCountTooltip', { count: orders.length })}
            sx={{ fontSize: { xs: '0.7rem', md: '0.8125rem' } }}
          />
        </Box>
      </KitchenHeader>
      
      {/* Mobil Menü Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
        }}
      >
        <MobileMenu onClose={handleMobileMenuToggle} />
      </Drawer>
      
      <ContentArea isFullscreen={isFullscreen}>
        {/* Sipariş Kartları Grid - Responsive sütunlar */}
        {/* Kompakt modda daha fazla sipariş göster (kartlar küçük olduğu için) */}
        <Grid container spacing={1.5}>
          {orders.slice(0, viewMode === 'compact' ? 24 : 12).map((order) => {
            const elapsedMinutes = calculateElapsedMinutes(order.siparisOlusturmaTarihi);
            // Dinamik renkleri hesapla
            const cardColors = getOrderCardColors(order.siparisDurumu, elapsedMinutes);
            // Ekran boyutuna göre ürün sayısını ayarla - butonlar için yer bırak
            const maxItems = screenSize === 'small' ? 1 : (screenSize === 'medium' ? 2 : 3);
            const displayItems = order.siparisDetaylari.slice(0, maxItems);
            const hasMoreItems = order.siparisDetaylari.length > maxItems;

            return (
              <Grid 
                item 
                xs={6}  // Çok küçük ekran: 2 sütun
                sm={4}  // Küçük ekran: 3 sütun  
                md={3}  // Orta ekran: 4 sütun
                lg={2}  // Büyük ekran: 6 sütun
                xl={2}  // Çok büyük ekran: 6 sütun
                key={order.id}
              >
                <OrderCard 
                  status={order.siparisDurumu}
                  elapsedMinutes={elapsedMinutes}
                  isFullscreen={isFullscreen}
                  screenSize={screenSize}
                  cardColors={cardColors}
                  viewMode={viewMode}
                  onClick={() => handleOpenOrderDetail(order)}
                  data-order-id={order.id}
                >
                  <CardContent sx={{ 
                    p: isFullscreen ? 2 : 1.5, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column' 
                  }}>
                    {/* Üst Kısım - Sipariş Bilgileri */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                        #{getShortOrderCode(order.siparisId)}
                      </Typography>
                      {/* 1. Satır: Masa numarası - Süre sayacı */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                          {getOrderSource(order)}
                        </Typography>
                        <TimerChip 
                          label={`${elapsedMinutes}dk`}
                          size="small"
                          elapsedMinutes={elapsedMinutes}
                          timeColor={cardColors.timeColor}
                          icon={<AccessTimeIcon />}
                        />
                      </Box>
                      
                      {/* 2. Satır: NOT yazısı - Durum bilgisi */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Box>
                          {order.siparisNotu ? (
                            <Typography variant="caption" sx={{ 
                              bgcolor: 'warning.light', 
                              color: 'warning.contrastText',
                              px: 0.5, 
                              py: 0.2, 
                              borderRadius: 0.5,
                              fontSize: '0.65rem',
                              fontWeight: 'bold'
                            }}>
                              📝 NOT
                            </Typography>
                          ) : (
                            <Box sx={{ height: '20px' }} /> // Boş alan bırak
                          )}
                        </Box>
                        <StatusChip 
                          label={order.siparisDurumu}
                          size="small"
                          status={order.siparisDurumu}
                        />
                      </Box>

                      {/* 3. Satır: Kurye Bilgisi (varsa) */}
                      {order.teslimAlanKuryeAdi && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" sx={{ 
                            bgcolor: 'info.light', 
                            color: 'info.contrastText',
                            px: 0.5, 
                            py: 0.2, 
                            borderRadius: 0.5,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.3
                          }}>
                            🚴 {order.teslimAlanKuryeAdi}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }} component="span">
                      {order.kullaniciAdi}
                    </Typography>

                    {/* Kompakt modda divider ve ürün listesini gizle */}
                    {viewMode === 'detailed' && (
                      <>
                        <Divider sx={{ my: 1 }} />

                        {/* Orta Kısım - Ürün Listesi */}
                        <Box sx={{ 
                          flex: 1, 
                          overflow: 'hidden', 
                          minHeight: (() => {
                            if (isFullscreen) return '120px';
                            return screenSize === 'small' ? '80px' : (screenSize === 'medium' ? '100px' : '140px');
                          })(),
                          maxHeight: (() => {
                            if (isFullscreen) return '200px';
                            return screenSize === 'small' ? '120px' : (screenSize === 'medium' ? '160px' : '220px');
                          })()
                        }}>
                          {displayItems.map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
                              {(item.imageUrl || item.urunResmi) && (
                                <Avatar
                                  src={item.imageUrl || item.urunResmi}
                                  alt={item.urunAdi}
                                  sx={{ 
                                    width: isFullscreen ? 40 : 32, 
                                    height: isFullscreen ? 40 : 32, 
                                    mr: 1.5 
                                  }}
                                  variant="rounded"
                                />
                              )}
                              {!(item.imageUrl || item.urunResmi) && (
                                <Avatar
                                  sx={{ 
                                    width: isFullscreen ? 40 : 32, 
                                    height: isFullscreen ? 40 : 32, 
                                    mr: 1.5, 
                                    bgcolor: 'grey.300' 
                                  }}
                                  variant="rounded"
                                >
                                  <RestaurantIcon sx={{ 
                                    fontSize: isFullscreen ? 22 : 18, 
                                    color: 'grey.600' 
                                  }} />
                                </Avatar>
                              )}
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  flex: 1, 
                                  fontSize: isFullscreen ? '0.95rem' : '0.85rem', 
                                  fontWeight: 'medium' 
                                }} 
                                component="div"
                              >
                                <Typography component="span" sx={{ 
                                  fontWeight: 'bold', 
                                  fontSize: isFullscreen ? '1rem' : '0.9rem' 
                                }}>
                                  {(item.adet || item.miktar || 1)}
                                </Typography>
                                {' x '}{item.urunAdi}
                                {/* ✅ Ürüne özel not (özelleştirme seçenekleri) */}
                                {item.aciklama && (
                                  <Typography 
                                    component="div" 
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      color: 'warning.main',
                                      fontStyle: 'italic',
                                      mt: 0.3,
                                      pl: 0.5
                                    }}
                                  >
                                    📝 {item.aciklama}
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                          ))}
                          
                          {hasMoreItems && (
                            <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic', fontSize: '0.75rem', fontWeight: 'medium', mt: 0.5 }} component="span">
                              +{order.siparisDetaylari.length - maxItems} ürün daha...
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}

                    {/* Alt Kısım - Aksiyon Butonları */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      mt: 1 
                    }}>
                      {/* Tüm butonlar aynı satırda */}
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        width: '100%',
                        px: 1 // Yanlardan biraz padding
                      }}>
                        {/* Hazır Butonu - beklemede ve hazırlanıyor durumlarında göster */}
                        {(order.siparisDurumu.toLowerCase() === 'beklemede' || 
                          order.siparisDurumu.toLowerCase() === 'hazırlanıyor') && permissions?.updateOrderStatus && (
                          <ActionButton
                            variant="contained"
                            color="success"
                            isFullscreen={isFullscreen}
                            onClick={(e) => {
                              e.stopPropagation();
                              updateOrderStatus(order.id, 'Hazır');
                            }}
                            startIcon={isMobile ? undefined : <CheckCircleIcon />}
                            title="Hazır"
                            sx={{ minWidth: isMobile ? '40px' : undefined }}
                          >
                            {isMobile ? <CheckCircleIcon /> : 'Hazır'}
                          </ActionButton>
                        )}
                        
                        {/* Teslim Edildi Butonu - SADECE hazır durumunda ve teslimat adresi OLMAYAN siparişlerde göster */}
                        {/* (QR Menü: masaNumarasi > 0, Kiosk: P- veya D-, Entegrasyon: HC-, Hızlı Satış: -HS-) */}
                        {order.siparisDurumu.toLowerCase() === 'hazır' && 
                         permissions?.updateOrderStatus && 
                         (
                           // QR Menü masa siparişleri (masa numarası var)
                           (order.masaNumarasi !== 0 && order.masaNumarasi !== null) ||
                           // Kiosk siparişleri (sipariş ID'si P- veya D- ile başlar)
                           (order.siparisId.startsWith('P-') || order.siparisId.startsWith('D-')) ||
                           // Hacıoğlu entegrasyon siparişleri (sipariş ID'si HC- ile başlar)
                           order.siparisId.startsWith('HC-') ||
                           // Hızlı Satış siparişleri (sipariş ID'si -HS- içerir)
                           order.siparisId.includes('-HS-')
                         ) && (
                          <ActionButton
                            variant="contained"
                            color="info"
                            isFullscreen={isFullscreen}
                            onClick={(e) => {
                              e.stopPropagation();
                              updateOrderStatus(order.id, 'Teslim Edildi');
                            }}
                            startIcon={isMobile ? undefined : <TableRestaurantIcon />}
                            title="Teslim Et"
                            sx={{ minWidth: isMobile ? '40px' : undefined }}
                          >
                            {isMobile ? <TableRestaurantIcon /> : 'Teslim'}
                          </ActionButton>
                        )}
                        
                        {/* Yazdır Butonu - showPrintButton yetkisi varsa göster */}
                        {permissions?.showPrintButton && (
                          <ActionButton
                            variant="outlined"
                            color="primary"
                            isFullscreen={isFullscreen}
                            className="print-button"
                            onClick={(e) => {
                              console.log('🖨️ [KITCHEN PRINT] Yazdır butonuna tıklandı:', {
                                siparisId: order.siparisId,
                                masaNumarasi: order.masaNumarasi,
                                showPrintButton: permissions?.showPrintButton
                              });
                              e.stopPropagation();
                              handlePrintOrder(order);
                            }}
                            startIcon={isMobile ? undefined : <PrintIcon />}
                            title="Yazdır"
                            sx={{ minWidth: isMobile ? '40px' : undefined }}
                          >
                            {isMobile ? <PrintIcon /> : 'Yazdır'}
                          </ActionButton>
                        )}
                        
                        {/* İptal butonu - her zaman aynı satırda göster */}
                        {permissions?.cancelOrders && !order.siparisDurumu.toLowerCase().includes('iptal') && (
                          <ActionButton
                            variant="outlined"
                            color="error"
                            isFullscreen={isFullscreen}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Bu siparişi iptal etmek istediğinizden emin misiniz?')) {
                                updateOrderStatus(order.id, 'İptal');
                              }
                            }}
                            startIcon={isMobile ? undefined : <CancelIcon />}
                            title="İptal Et"
                            sx={{ minWidth: isMobile ? '40px' : undefined }}
                          >
                            {isMobile ? <CancelIcon /> : 'İptal'}
                          </ActionButton>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </OrderCard>
              </Grid>
            );
          })}
        </Grid>

        {/* Boş Durum */}
        {orders.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '50vh',
            textAlign: 'center'
          }}>
            <RestaurantIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {t('kitchenScreen.emptyState.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('kitchenScreen.emptyState.description')}
            </Typography>
          </Box>
        )}
      </ContentArea>

      {/* Sipariş Detay Modalı */}
      <Dialog
        open={orderDetailModalOpen}
        onClose={() => setOrderDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Sipariş Detayları - #{selectedOrder?.siparisId}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              {selectedOrder ? getOrderSource(selectedOrder) : ''} • {selectedOrder?.kullaniciAdi}
            </Typography>
          </Box>
          <StatusChip 
            label={selectedOrder?.siparisDurumu || ''}
            status={selectedOrder?.siparisDurumu || ''}
          />
        </DialogTitle>
        
        <DialogContent>
          <List>
            {selectedOrder?.siparisDetaylari.map((item, index) => (
              <ListItem key={index} divider>
                <ListItemAvatar>
                  {(item.imageUrl || item.urunResmi) ? (
                    <Avatar
                      src={item.imageUrl || item.urunResmi}
                      alt={item.urunAdi}
                      variant="rounded"
                      sx={{ width: 56, height: 56 }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{ width: 56, height: 56, bgcolor: 'grey.200' }}
                    >
                      <RestaurantIcon color="disabled" />
                    </Avatar>
                  )}
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 2 }}
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {item.urunAdi}
                      </Typography>
                      {permissions?.showPrices && (
                        <Typography variant="h6" color="primary">
                          {item.toplamFiyat.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body1" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                        <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'text.primary' }}>
                          Adet: 
                        </Typography>
                        <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'primary.main', ml: 0.5 }}>
                          {(item.adet || item.miktar || 1)}
                        </Typography>
                        {permissions?.showPrices && (
                          <Typography component="span" sx={{ ml: 1, fontSize: '0.95rem' }}>
                            • Birim Fiyat: {item.birimFiyat.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                          </Typography>
                        )}
                      </Typography>
                      {/* ✅ Ürüne özel not (özelleştirme) */}
                      {item.aciklama && (
                        <Box sx={{ 
                          mt: 1, 
                          p: 1, 
                          bgcolor: 'warning.light', 
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'warning.main'
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontStyle: 'italic', 
                            color: 'warning.contrastText',
                            fontWeight: 'medium'
                          }}>
                            📝 {item.aciklama}
                          </Typography>
                        </Box>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Sipariş Notu */}
          {selectedOrder?.siparisNotu && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  📝 Sipariş Notu
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'warning.light', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'warning.main'
                }}>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'warning.contrastText' }}>
                    "{selectedOrder.siparisNotu}"
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          
          {permissions?.showPrices && (
            <Box>
              {/* Ürünler Toplamı */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1">
                  Ürünler Toplamı:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {(() => {
                    const urunlerToplami = selectedOrder?.siparisDetaylari.reduce((total, item) => total + item.toplamFiyat, 0) || 0;
                    return urunlerToplami.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
                  })()}
                </Typography>
              </Box>
              
              {/* Teslimat Ücreti */}
              {(() => {
                const urunlerToplami = selectedOrder?.siparisDetaylari.reduce((total, item) => total + item.toplamFiyat, 0) || 0;
                const teslimatUcreti = (selectedOrder?.toplamTutar || 0) - urunlerToplami;
                
                if (teslimatUcreti > 0) {
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body1">
                        Teslimat Ücreti:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {teslimatUcreti.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}
              
              <Divider sx={{ my: 1 }} />
              
              {/* Genel Toplam */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Toplam Tutar:
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  {selectedOrder?.toplamTutar.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOrderDetailModalOpen(false)}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcı Menüsü */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: { width: 200, mt: 1 }
        }}
      >
        <MenuItem onClick={handleLogout}>
          {t('kitchenScreen.header.logout')}
        </MenuItem>
      </Menu>
    </KitchenContainer>
  );
};

export default KitchenScreen;
